import { PlotSeries } from "@/shared/state/visualization";
import { numberTimeToBigIntNanosec } from "@/shared/time";

import { SeriesAlignment } from "./alignment";

interface ResolveAlignedSeekTargetParams {
  series: PlotSeries[];
  alignment: SeriesAlignment;
  /** The timer's current absolute time. */
  currentTimeNs: bigint;
  /** The clicked x position, in chart seconds. */
  clickedSeconds: number;
}

/**
 * The series plotted on an aligned panel and how they are aligned: what a
 * click needs to resolve into a seek target.
 */
export type SeekAlignment = Pick<
  ResolveAlignedSeekTargetParams,
  "series" | "alignment"
>;

/**
 * The absolute timestamp a click on an aligned chart seeks to, or
 * `undefined` when the click has no seek meaning.
 *
 * An aligned x position names a moment in every session at once, so the
 * click resolves against the sessions containing the playhead, narrowed to
 * those whose clipped span also contains the clicked moment. When several
 * qualify (overlapping sessions, or per-series bases within one session) the
 * one whose playhead line sits nearest the clicked x wins, so a click at or
 * near a visible line stays in that line's session. A click no session can
 * serve resolves to nothing: past the containing session's own span (legal
 * chart area, because the domain covers the longest session), or with the
 * playhead sitting between sessions. A hidden series' session is not
 * seekable.
 */
export function resolveAlignedSeekTarget({
  series,
  alignment,
  currentTimeNs,
  clickedSeconds,
}: ResolveAlignedSeekTargetParams): bigint | undefined {
  if (!Number.isFinite(clickedSeconds)) {
    // BigInt(NaN) and BigInt(Infinity) throw, which would take the click
    // handler down with them.
    return undefined;
  }
  const clickedNs = numberTimeToBigIntNanosec(clickedSeconds, "s");

  let nearest: { target: bigint; distanceFromCurrentTime: bigint } | undefined;
  for (const seriesItem of series) {
    if (!seriesItem.visible) {
      continue;
    }
    const aligned = alignment.seriesById[seriesItem.id];
    if (aligned === undefined) {
      continue;
    }
    if (
      currentTimeNs < aligned.sourceStartTime ||
      currentTimeNs > aligned.sourceEndTime
    ) {
      continue;
    }
    const target = aligned.basis + clickedNs;
    if (target < aligned.sourceStartTime || target > aligned.sourceEndTime) {
      continue;
    }
    // How far the seek would move the playhead in absolute time, which on
    // the chart is the click's distance from this series' playhead line.
    const distanceFromCurrentTime =
      target > currentTimeNs ? target - currentTimeNs : currentTimeNs - target;
    if (
      nearest === undefined ||
      distanceFromCurrentTime < nearest.distanceFromCurrentTime
    ) {
      nearest = { target, distanceFromCurrentTime };
    }
  }
  return nearest?.target;
}
